import HoverState from '@engoo/react-hover-state';
import React, { FC, ReactNode } from 'react';
import {
  BLUE_H,
  BLUE_G,
  DARK_BLUE_A,
  DARK_BLUE_B,
  LIGHT_BLUE_A,
  RED_H,
  TRANSPARENT_RED_H,
  TRANSPARENT_BLUE_H,
  RED_I,
} from 'app/constants/Colors';
import { createStyles, keyframes } from 'app/utils/css';
import BaseButton from 'app/components/common/BaseButton';

export enum ColorScheme {
  Facebook  = 'facebook',
  Red       = 'red',
  Secondary = 'secondary',
}

export enum Size {
  Tiny = '0.25em 1em',
  Small = '0.75em 2em',
  Normal = '0.75em 3em',
}

interface IStyleProps {
  fullWidth?: boolean;
  disabled?: boolean;
  pulse?: boolean;
  size?: Size;
  colorScheme?: ColorScheme;
}

interface IProps extends IStyleProps {
  children: (className: string) => ReactNode;
}

const ButtonClassNameA: React.SFC<IProps> = ({
  children,
  fullWidth = false,
  disabled = false,
  pulse = false,
  colorScheme,
  size = Size.Normal,
}) => (
  <Styles>{(css) =>
    <HoverState>{(isHovered) =>
      children(css('button', { isHovered, size, fullWidth, disabled, colorScheme, pulse }))
    }</HoverState>
  }</Styles>
);

const pulseRedKeyframes = keyframes({
  '0%': {
    boxShadow: `0 0 0 0px ${RED_H}`,
  },
  '100%': {
    boxShadow: `0 0 0 10px ${TRANSPARENT_RED_H}`,
  },
});

const pulseBlueKeyframes = keyframes({
  '0%': {
    boxShadow: `0 0 0 0px ${BLUE_H}`,
  },
  '100%': {
    boxShadow: `0 0 0 10px ${TRANSPARENT_BLUE_H}`,
  },
});

const Styles = createStyles({
  button: ({ colorScheme, disabled, isHovered, fullWidth, size, pulse }) => [
    {
      display: 'inline-block',
      color: 'white',
      border: `2px solid ${BLUE_H}`,
      borderRadius: '2em',
      boxSizing: 'border-box',
      cursor: 'pointer',
      textDecoration: 'none',
      whiteSpace: 'nowrap',
      textAlign: 'center',
    },

    size === Size.Normal ? {
      padding: '0.75em 3em',
    } :
    size === Size.Small ? {
      padding: '0.75em 2em',
    } :
    size === Size.Tiny ? {
      padding: '0.25em 1em',
    } : undefined,

    colorScheme === ColorScheme.Facebook ? {
      backgroundColor: DARK_BLUE_A,
      borderColor: DARK_BLUE_A,
      ':active': {
        backgroundColor: DARK_BLUE_B,
        borderColor: DARK_BLUE_B,
      },
    } :
    colorScheme === ColorScheme.Red ? {
      backgroundColor: RED_H,
      borderColor: RED_H,
      ':active': {
        backgroundColor: RED_I,
        borderColor: RED_I,
      },
    } :
    colorScheme === ColorScheme.Secondary ? {
      backgroundColor: 'transparent',
      color: BLUE_H,
      ':active': {
        backgroundColor: LIGHT_BLUE_A,
      },
    } :
    /* else */ {
      backgroundColor: BLUE_H,
      ':active': {
        backgroundColor: BLUE_G,
        borderColor: BLUE_G,
      },
    },

    disabled ? {
      opacity: '0.5',
      cursor: 'default',
    } :
    isHovered ? (
      colorScheme === ColorScheme.Facebook ? {
        backgroundColor: DARK_BLUE_B,
        borderColor: DARK_BLUE_B,
      } :
      colorScheme === ColorScheme.Red ? {
        backgroundColor: RED_I,
        borderColor: RED_I,
      } :
      colorScheme === ColorScheme.Secondary ? {
        backgroundColor: LIGHT_BLUE_A,
      } :
      /* else */ {
        backgroundColor: BLUE_G,
        borderColor: BLUE_G,
      }
    ) : null,

    // Stop pulsing when button is disabled
    !disabled && pulse && (colorScheme === ColorScheme.Red ? ({
      animation: `${pulseRedKeyframes} ease-in-out 1.5s infinite`,
    }) : {
      animation: `${pulseBlueKeyframes} ease-in-out 1.5s infinite`,
    }),

    fullWidth && {
      width: '100%',
      whiteSpace: 'normal',
    },
  ],
});

/**
 * A customizable button component.
 * This is an example TS doc that is extracted to Storybook Docs.
 */
export const ButtonA: FC<
  IStyleProps & {
    onClick?: React.MouseEventHandler<HTMLButtonElement>,
    type?: 'button' | 'submit';
  }
> = ({ children, disabled, onClick, type, ...rest }) => (
  <ButtonClassNameA disabled={disabled} {...rest}>{(className) =>
    <BaseButton
      type={type || 'button'}
      className={className}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </BaseButton>
  }</ButtonClassNameA>
);

export default ButtonClassNameA;
